import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { fetchUsageMetrics } from '../services/adminService';
import styles from './AdminMonitorMetricsPage.module.css';

// AI drafting & chat is unlimited (never metered) but IS counted, so the team can watch the
// one uncapped cost driver. Shown as a plain action count with no allowance.
const AI_COLUMN = { key: 'ai_actions', label: 'AI actions' };

const AUDIENCE_FILTERS = [
  { value: 'all',      label: 'All' },
  { value: 'founding', label: 'Founding' },
  { value: 'public',   label: 'Public' },
  { value: 'other',    label: 'Other' },
];

// Mirrors the `status` the backend derives from the trial window + subscriptions table.
const STATUS_FILTERS = [
  { value: 'all',        label: 'Any status' },
  { value: 'trial',      label: 'In trial' },
  { value: 'expired',    label: 'Trial expired' },
  { value: 'leads_out',  label: 'Out of leads' },
  { value: 'subscribed', label: 'Subscribed' },
  { value: 'developer',  label: 'Developer' },
];
const STATUS_LABELS = {
  trial: 'In trial', expired: 'Expired', leads_out: 'Out of leads',
  subscribed: 'Subscribed', developer: 'Developer',
};

// A metric is "near cap" at 90%+ consumed — the same threshold the usage bar turns amber at.
const NEAR_CAP_PCT = 90;

const pctOf = (m) => (m && m.allowance > 0 ? Math.min(100, Math.round((m.used / m.allowance) * 100)) : 0);

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

// Compact "how long ago" for the last-usage column — the fastest way to spot a dormant user.
function fmtRelative(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return fmtDate(iso);
}

// Days remaining on the trial clock; negative once it has elapsed.
function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  return Math.ceil(ms / 86400000);
}

// Renders one metered cell: "used / allowance" with a usage bar. Dev rows render "Unlimited".
function MetricCell({ data }) {
  if (!data) return <span className={styles.metricEmpty}>—</span>;
  const { used = 0, allowance = 0 } = data;
  const pct = pctOf(data);
  return (
    <div className={styles.metricCell}>
      <div className={styles.metricNums}>
        <span className={styles.metricUsed}>{used.toLocaleString()}</span>
        <span className={styles.metricAllow}>/ {allowance.toLocaleString()}</span>
      </div>
      <div className={styles.bar} title={`${pct}% consumed`}>
        <div className={`${styles.barFill} ${pct >= NEAR_CAP_PCT ? styles.barHigh : ''}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function csvEscape(v) {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export default function AdminMonitorMetricsPage() {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: null, forbidden: false });
  const [payload, setPayload] = useState(null); // { users, metrics, count, generatedAt }
  const [search, setSearch] = useState('');
  const [audience, setAudience] = useState('all');
  const [status, setStatus] = useState('all');
  const [sort, setSort] = useState({ key: 'createdAt', dir: 'desc' });

  async function load() {
    setState({ loading: true, error: null, forbidden: false });
    try {
      const data = await fetchUsageMetrics();
      setPayload(data);
      setState({ loading: false, error: null, forbidden: false });
    } catch (err) {
      if (err.status === 403) setState({ loading: false, error: null, forbidden: true });
      else if (err.status === 401) setState({ loading: false, error: 'Please sign in to view this page.', forbidden: false });
      else setState({ loading: false, error: err.message || 'Failed to load usage metrics.', forbidden: false });
    }
  }

  useEffect(() => { load(); }, []);

  const metrics = useMemo(() => payload?.metrics || [], [payload]);

  // Sort by PERCENTAGE consumed, not absolute units: founding users are on 150 leads and
  // public users on 30, so "3 used" and "8 used" only become comparable as 10% vs 5%.
  // Ranking by raw counts would bury the user who is actually about to run out.
  function sortValue(user, key) {
    if (key.startsWith('metric:')) {
      const m = user.metrics?.[key.slice(7)];
      return m ? pctOf(m) : -1;
    }
    if (key === 'ai') return user.ai?.actions ?? -1;
    if (key === 'createdAt') return new Date(user.createdAt || 0).getTime();
    if (key === 'lastActiveAt') return user.lastActiveAt ? new Date(user.lastActiveAt).getTime() : -1;
    return (user[key] || '').toString().toLowerCase();
  }

  const rows = useMemo(() => {
    let list = payload?.users ? [...payload.users] : [];

    const q = search.trim().toLowerCase();
    if (q) list = list.filter((u) => `${u.name || ''} ${u.email || ''}`.toLowerCase().includes(q));
    if (audience !== 'all') {
      list = list.filter((u) => (audience === 'other' ? !u.audience : u.audience === audience));
    }
    if (status !== 'all') list = list.filter((u) => u.status === status);

    const dir = sort.dir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      const av = sortValue(a, sort.key);
      const bv = sortValue(b, sort.key);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return list;
  }, [payload, search, audience, status, sort]);

  // Cohort roll-up over the CURRENTLY FILTERED rows, so narrowing the filters re-scopes the
  // totals. Dev accounts are excluded — they're unmetered, so folding them in would dilute
  // every percentage with rows that can never consume an allowance.
  const summary = useMemo(() => {
    const metered = rows.filter((u) => !u.unlimited);
    const totals = metrics.map((m) => {
      let used = 0; let allowance = 0; let nearCap = 0;
      for (const u of metered) {
        const cell = u.metrics?.[m.key];
        if (!cell) continue;
        used += cell.used || 0;
        allowance += cell.allowance || 0;
        if (pctOf(cell) >= NEAR_CAP_PCT) nearCap += 1;
      }
      return { ...m, used, allowance, nearCap, pct: allowance > 0 ? Math.round((used / allowance) * 100) : 0 };
    });
    const aiActions = metered.reduce((n, u) => n + (u.ai?.actions || 0), 0);
    return {
      metered: metered.length,
      totals,
      aiActions,
      nearCapUsers: metered.filter((u) => metrics.some((m) => pctOf(u.metrics?.[m.key]) >= NEAR_CAP_PCT)).length,
      activeTrials: metered.filter((u) => u.status === 'trial').length,
      idle: metered.filter((u) => !u.lastActiveAt).length,
    };
  }, [rows, metrics]);

  function toggleSort(key) {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));
  }

  // Exports exactly what's on screen (current filters + sort), so a filtered view is the
  // unit of sharing. One column pair per metric keeps it pivot-friendly in Sheets/Excel.
  function exportCsv() {
    const head = ['Name', 'Email', 'Audience', 'Plan', 'Status', 'Trial ends', 'Signed up', 'Last active'];
    for (const m of metrics) head.push(`${m.label} used`, `${m.label} allowance`, `${m.label} %`);
    head.push('AI actions');

    const lines = [head.map(csvEscape).join(',')];
    for (const u of rows) {
      const cells = [
        u.name, u.email, u.audience, u.plan, STATUS_LABELS[u.status] || u.status,
        u.trialEndsAt ? new Date(u.trialEndsAt).toISOString().slice(0, 10) : '',
        u.createdAt ? new Date(u.createdAt).toISOString().slice(0, 10) : '',
        u.lastActiveAt ? new Date(u.lastActiveAt).toISOString() : '',
      ];
      for (const m of metrics) {
        const cell = u.metrics?.[m.key];
        if (u.unlimited) cells.push('Unlimited', 'Unlimited', '');
        else if (!cell) cells.push('', '', '');
        else cells.push(cell.used, cell.allowance, pctOf(cell));
      }
      cells.push(u.ai?.actions ?? '');
      lines.push(cells.map(csvEscape).join(','));
    }

    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `repbuddy-usage-metrics-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function SortHeader({ label, colKey, align }) {
    const active = sort.key === colKey;
    return (
      <th
        className={`${styles.th} ${align === 'right' ? styles.thRight : ''} ${active ? styles.thActive : ''}`}
        onClick={() => toggleSort(colKey)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSort(colKey); }
        }}
        tabIndex={0}
        role="columnheader"
        aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
        title={`Sort by ${label}`}
      >
        <span className={styles.thInner}>
          {label}
          <span className={styles.sortCaret} aria-hidden="true">{active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
        </span>
      </th>
    );
  }

  const colCount = 7 + metrics.length;

  const errorNotice = (
    <div className={styles.notice}>
      <h2 className={styles.noticeTitle}>Couldn't load usage metrics</h2>
      <p className={styles.noticeText}>{state.error}</p>
      <button className={styles.retryBtn} onClick={load}>Retry</button>
    </div>
  );

  // `payload` is only ever set by a successful (therefore authorized) response, so it doubles
  // as "the server has confirmed this caller is a developer". Refreshes keep it, so the table
  // stays on screen while reloading instead of dropping back to the boot screen.
  const authorized = payload !== null;

  // Non-developers are sent to the app rather than shown an internal page. The client can't
  // make this call itself — DEV_EMAILS never leaves the backend — so it reacts to the 403.
  if (state.forbidden) return <Navigate to="/app" replace />;

  // Nothing internal renders until the endpoint has authorized the caller: no branding, no
  // column headers, not even the page title. Otherwise a non-developer would see the whole
  // dashboard chrome for the moment before the redirect lands. Deliberately identical to
  // ProtectedRoute's loading screen, so this URL looks like any other while it resolves.
  if (!authorized) {
    return <div className={styles.boot}>{state.error ? errorNotice : 'Loading…'}</div>;
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.brand}>
          <span className={styles.brandName}>RepBuddy</span>
          <span className={styles.brandDivider} />
          <span className={styles.brandSub}>Usage Metrics · Internal</span>
        </div>
        <button className={styles.backBtn} onClick={() => navigate('/app')}>⬅️ Back to app</button>
      </header>

      <div className={styles.wrap}>
        {state.error ? errorNotice : (
          <>
            <div className={styles.titleRow}>
              <div>
                <h1 className={styles.title}>User usage metrics</h1>
                <p className={styles.subtitle}>
                  {state.loading
                    ? 'Loading…'
                    : `${payload?.count ?? 0} users · updated ${payload?.generatedAt ? new Date(payload.generatedAt).toLocaleTimeString() : ''}`}
                </p>
              </div>
              <div className={styles.titleActions}>
                <button className={styles.refreshBtn} onClick={exportCsv} disabled={state.loading || rows.length === 0}>
                  ↓ Export CSV
                </button>
                <button className={styles.refreshBtn} onClick={load} disabled={state.loading}>
                  {state.loading ? 'Refreshing…' : '↻ Refresh'}
                </button>
              </div>
            </div>

            {!state.loading && (
              <div className={styles.summary}>
                {summary.totals.map((t) => (
                  <div key={t.key} className={styles.statCard}>
                    <span className={styles.statLabel}>{t.label}</span>
                    <span className={styles.statValue}>
                      {t.used.toLocaleString()}
                      <span className={styles.statOf}> / {t.allowance.toLocaleString()}</span>
                    </span>
                    <div className={styles.bar}>
                      <div className={`${styles.barFill} ${t.pct >= NEAR_CAP_PCT ? styles.barHigh : ''}`} style={{ width: `${t.pct}%` }} />
                    </div>
                    <span className={styles.statFoot}>
                      {t.pct}% of pool{t.nearCap > 0 ? ` · ${t.nearCap} near cap` : ''}
                    </span>
                  </div>
                ))}
                <div className={styles.statCard}>
                  <span className={styles.statLabel}>Cohort</span>
                  <span className={styles.statValue}>{summary.metered.toLocaleString()}</span>
                  <span className={styles.statFoot}>
                    {summary.activeTrials} in trial · {summary.nearCapUsers} near cap · {summary.idle} never active
                  </span>
                </div>
              </div>
            )}

            <div className={styles.toolbar}>
              <input
                className={styles.searchInput}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or email…"
                aria-label="Search users by name or email"
              />
              <div className={styles.segmented} role="group" aria-label="Filter by audience">
                {AUDIENCE_FILTERS.map((f) => (
                  <button
                    key={f.value}
                    className={`${styles.segBtn} ${audience === f.value ? styles.segActive : ''}`}
                    onClick={() => setAudience(f.value)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <select
                className={styles.select}
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                aria-label="Filter by status"
              >
                {STATUS_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
              <span className={styles.resultCount}>{rows.length} shown</span>
            </div>

            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <SortHeader label="User" colKey="name" />
                    <SortHeader label="Audience" colKey="audience" />
                    <SortHeader label="Plan" colKey="plan" />
                    <SortHeader label="Status" colKey="status" />
                    <SortHeader label="Signed up" colKey="createdAt" />
                    <SortHeader label="Last active" colKey="lastActiveAt" />
                    {metrics.map((m) => (
                      <SortHeader key={m.key} label={m.label} colKey={`metric:${m.key}`} align="right" />
                    ))}
                    <SortHeader label={AI_COLUMN.label} colKey="ai" align="right" />
                  </tr>
                </thead>
                <tbody>
                  {state.loading ? (
                    <tr><td className={styles.stateCell} colSpan={colCount}>Loading users…</td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td className={styles.stateCell} colSpan={colCount}>No users match your filters.</td></tr>
                  ) : (
                    rows.map((u) => {
                      const daysLeft = u.status === 'trial' ? daysUntil(u.trialEndsAt) : null;
                      const relative = fmtRelative(u.lastActiveAt);
                      return (
                        <tr key={u.id} className={styles.row}>
                          <td className={styles.userCell}>
                            <span className={styles.userName}>
                              {u.name || '—'}
                              {u.isDev && <span className={styles.devBadge}>DEV</span>}
                            </span>
                            <span className={styles.userEmail}>{u.email}</span>
                          </td>
                          <td>
                            {u.audience
                              ? <span className={`${styles.tag} ${u.audience === 'founding' ? styles.tagFounding : ''}`}>{u.audience}</span>
                              : <span className={styles.metricEmpty}>—</span>}
                          </td>
                          <td className={styles.planCell}>
                            {u.plan
                              ? <>{u.plan}{u.paidPlan && <span className={styles.paidBadge}>PAID</span>}</>
                              : <span className={styles.metricEmpty}>—</span>}
                          </td>
                          <td>
                            <span className={`${styles.tag} ${styles[`status_${u.status}`] || ''}`}>
                              {STATUS_LABELS[u.status] || u.status}
                            </span>
                            {daysLeft !== null && (
                              <span className={styles.statusSub}>{daysLeft > 0 ? `${daysLeft}d left` : 'ends today'}</span>
                            )}
                          </td>
                          <td className={styles.dateCell}>{fmtDate(u.createdAt)}</td>
                          <td className={styles.dateCell}>
                            {relative
                              ? <span title={new Date(u.lastActiveAt).toLocaleString()}>{relative}</span>
                              : <span className={styles.metricEmpty}>never</span>}
                          </td>
                          {metrics.map((m) => (
                            <td key={m.key} className={styles.numCell}>
                              {/* Dev accounts bypass metering, so they have no balance to show. */}
                              {u.unlimited
                                ? <span className={styles.unlimited}>Unlimited</span>
                                : <MetricCell data={u.metrics?.[m.key]} />}
                            </td>
                          ))}
                          <td className={styles.numCell}>
                            {u.ai?.actions
                              ? <span className={styles.aiCount}>{u.ai.actions.toLocaleString()}</span>
                              : <span className={styles.metricEmpty}>0</span>}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
